
import React, { useState } from "react";
import { ReportAPI } from "../../api/apiClient";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ManPowerStatementResourceWiseReport({
  projectId,
  projectName
}: {
  projectId: number;
  projectName: string;
}) {

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [expandDates, setExpandDates] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [isPdfExport, setIsPdfExport] = useState(false);

  const loadReport = async () => {

    if (!fromDate || !toDate) {
      alert("Select date range");
      return;
    }

    setLoading(true);

    try {

      const result =
        await ReportAPI.getManPowerStatementResourceWise({
          project: projectId,
          qDate: fromDate,
          endDate: toDate,
          vendor: -1,
          cmp: 0
        });

      setData(result);
      setShowReport(true);

    } catch (err) {
      console.error(err);
      alert("Failed to load report");
    }

    setLoading(false);
  };

  const goBack = () => {
    setShowReport(false);
  };

  const formatDate = (date: string) => {

    const d = new Date(date);

    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short" });
    const year = d.getFullYear();

    return (
      <div className="text-center leading-tight">
        <div className="whitespace-nowrap text-[11px]">{day}-{month}</div>
        <div className="text-[10px] text-gray-200">{year}</div>
      </div>
    );
  };

  const formatPdfDate = (date: string) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-GB", { month: "short" });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const dates = Array.from(new Set(data.map((x: any) => x.date))).sort();

  const sortedData = [...data].sort((a, b) => {

    if (a.componentGroup !== b.componentGroup)
      return a.componentGroup.localeCompare(b.componentGroup);

    if (a.component !== b.component)
      return a.component.localeCompare(b.component);

    return (a.vendor || "").localeCompare(b.vendor || "");
  });

  const grouped: any = {};
  const groupOrder: string[] = [];
  const componentOrder: any = {};

  sortedData.forEach((row: any) => {

    const vendorName = row.vendor || row.component;

    if (!grouped[row.componentGroup]) {
      grouped[row.componentGroup] = {};
      groupOrder.push(row.componentGroup);
      componentOrder[row.componentGroup] = [];
    }

    if (!grouped[row.componentGroup][row.component]) {
      grouped[row.componentGroup][row.component] = {};
      componentOrder[row.componentGroup].push(row.component);
    }

    if (!grouped[row.componentGroup][row.component][vendorName]) {
      grouped[row.componentGroup][row.component][vendorName] = {};
    }

    if (!grouped[row.componentGroup][row.component][vendorName][row.date]) {
      grouped[row.componentGroup][row.component][vendorName][row.date] = 0;
    }

    grouped[row.componentGroup][row.component][vendorName][row.date] += Number(row.onRoll);
  });

  const exportPDF = () => {

    setIsPdfExport(true);

    const pdf = new jsPDF("l", "mm", "a4");

    const datesPerPage = 13;

    const totalPages = expandDates
      ? Math.ceil(dates.length / datesPerPage)
      : 1;

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {

      const pageDates = expandDates
        ? dates.slice(pageIndex * datesPerPage, (pageIndex + 1) * datesPerPage)
        : [];

      const isLastPage = pageIndex === totalPages - 1;

      const headers = [
        "Resource",
        ...(expandDates
          ? pageDates.map((d) => {
              const dt = new Date(d);
              const day = dt.getDate().toString().padStart(2, "0");
              const mon = dt.toLocaleString("en-GB", { month: "short" });
              const yr = dt.getFullYear();
              return `${day}-${mon}\n${yr}`;
            })
          : []),
        ...(isLastPage ? ["Onroll"] : [])
      ];

      const body: any[] = [];

      groupOrder.forEach((group) => {

        body.push([
          group,
          ...(expandDates ? Array(pageDates.length).fill("") : []),
          ...(isLastPage ? [""] : [])
        ]);

        const components = grouped[group];

        componentOrder[group].forEach((component: string) => {

          const vendors = components[component];

          const row: any[] = [component];

          if (expandDates) {

            pageDates.forEach((d) => {

              const value =
                Object.values(vendors).reduce((sum: number, v: any) => {
                  return sum + (v[d] || 0);
                }, 0);

              row.push(value || "");
            });
          }

          if (isLastPage) {

            const total =
              Object.values(vendors).reduce((sum: number, v: any) => {
                return sum + Object.values(v).reduce((a: number, b: any) => a + Number(b), 0);
              }, 0);

            row.push(total.toFixed(2));
          }

          body.push(row);

          const key = group + "_" + component;

          if (expanded[key]) {

            Object.keys(vendors).forEach((vendor) => {

              const vendorRow: any[] = ["   " + vendor];

              if (expandDates) {

                pageDates.forEach((d) => {
                  vendorRow.push(vendors[vendor][d] ?? "");
                });

              }

              if (isLastPage) {

                const vendorTotal =
                  Object.values(vendors[vendor])
                    .reduce((a: number, b: any) => a + Number(b), 0)
                    .toFixed(2);

                vendorRow.push(vendorTotal);
              }

              body.push(vendorRow);

            });

          }

        });

      });

      if (pageIndex > 0) pdf.addPage();

      if (pageIndex === 0) {

        pdf.setFontSize(14);

        pdf.text(
          "Manpower Report Resource Wise",
          pdf.internal.pageSize.getWidth() / 2,
          10,
          { align: "center" }
        );

        pdf.setFontSize(11);

        pdf.text(`Project : ${projectName}`, 14, 16);

        pdf.text(
          `Date Range : ${formatPdfDate(fromDate)} to ${formatPdfDate(toDate)}`,
          14,
          22
        );
      }

      autoTable(pdf, {
        head: [headers],
        body,
        startY: pageIndex === 0 ? 28 : 14,
        styles: {
          fontSize: 8,
          cellPadding: 1.5,
          halign: "center"
        },
        headStyles: {
          fillColor: [91, 76, 84]
        },
        columnStyles: {
          0: { halign: "left" }
        },
        margin: { top: 14, bottom: 10 }
      });

      pdf.setFontSize(9);

      pdf.text(
        `Page ${pageIndex + 1} of ${totalPages}`,
        pdf.internal.pageSize.getWidth() / 2,
        pdf.internal.pageSize.getHeight() - 5,
        { align: "center" }
      );
    }

    pdf.save("Manpower_Report_Resource_Wise.pdf");

    setIsPdfExport(false);
  };

  return (
    <div className="p-4">

      {!showReport && (

        <div className="flex flex-col gap-4 max-w-md">

          <h2 className="text-xl font-semibold">
            ManPower Statement Resource Wise
          </h2>

          <input
            type="date"
            className="border p-2 rounded"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />

          <input
            type="date"
            className="border p-2 rounded"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />

          <button
            className="bg-blue-600 text-white px-6 py-2 rounded"
            onClick={loadReport}
          >
            Run Report
          </button>

        </div>
      )}

      {showReport && (

        <div id="reportContainer" style={{ width: "1000px", padding: "10px" }}>

          <div className="flex items-center justify-between mb-4">

            <div>
              <h2 className="text-xl font-semibold">
                Manpower Report Resource Wise
              </h2>
              <div className="text-sm text-gray-600">
                Project : <span className="font-semibold">{projectName}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">

              {!isPdfExport && (
                <div
                  className="cursor-pointer flex items-center font-semibold"
                  onClick={() => setExpandDates(!expandDates)}
                >
                  <span className="border w-5 h-5 inline-flex items-center justify-center mr-2">
                    {expandDates ? "-" : "+"}
                  </span>
                  Total
                </div>
              )}

              <button
                className="bg-green-600 text-white px-4 py-2 rounded"
                onClick={exportPDF}
              >
                Export PDF
              </button>

            </div>

          </div>

          {!loading && groupOrder.length > 0 && (

            <>
              <table className="text-sm border w-full">

                <thead>
                  <tr className="bg-[#5b4c54] text-white">

                    <th className="p-2 text-left">Resource</th>

                    {expandDates && dates.map((d) => (
                      <th key={d} className="px-1 py-1 text-center text-[10px]" style={{ minWidth: "48px" }}>
                        {formatDate(d)}
                      </th>
                    ))}

                    <th className="p-2 text-right">Onroll</th>

                  </tr>
                </thead>

                <tbody>

                  {groupOrder.map((group) => {

                    const components = grouped[group];

                    return (
                      <React.Fragment key={group}>

                        <tr className="bg-gray-200 font-semibold">
                          <td className="p-2">{group}</td>
                          {expandDates && dates.map((d) => <td key={d}></td>)}
                          <td></td>
                        </tr>

                        {componentOrder[group].map((component: string) => {

                          const vendors = components[component];

                          const total =
                            Object.values(vendors).reduce((sum: number, v: any) => {
                              return sum + Object.values(v).reduce((a: number, b: any) => a + Number(b), 0);
                            }, 0);

                          const key = group + "_" + component;

                          return (
                            <React.Fragment key={component}>

                              <tr
                                className="cursor-pointer bg-gray-100"
                                onClick={() =>
                                  setExpanded({
                                    ...expanded,
                                    [key]: !expanded[key]
                                  })
                                }
                              >

                                <td className="p-2 pl-6">

                                  {!isPdfExport && (
                                    <span className="font-bold mr-2">
                                      {expanded[key] ? "−" : "+"}
                                    </span>
                                  )}

                                  {component}

                                </td>

                                {expandDates && dates.map((d) => {

                                  const value =
                                    Object.values(vendors).reduce((sum: number, v: any) => {
                                      return sum + (v[d] || 0);
                                    }, 0);

                                  return (
                                    <td key={d} className="text-center text-[11px]">
                                      {value || ""}
                                    </td>
                                  );

                                })}

                                <td className="p-2 text-right">
                                  {total.toFixed(2)}
                                </td>

                              </tr>

                              {expanded[key] &&
                                Object.keys(vendors).map((vendor) => (

                                  <tr key={vendor}>

                                    <td className="p-2 pl-12 text-gray-700">
                                      {vendor}
                                    </td>

                                    {expandDates && dates.map((d) => (
                                      <td key={d} className="text-center text-[11px]">
                                        {vendors[vendor][d] ?? ""}
                                      </td>
                                    ))}

                                    <td className="p-2 text-right">
                                      {
                                        Object.values(vendors[vendor])
                                          .reduce((a: number, b: any) => a + Number(b), 0)
                                          .toFixed(2)
                                      }
                                    </td>

                                  </tr>

                                ))
                              }

                            </React.Fragment>
                          )
                        })}

                      </React.Fragment>
                    )
                  })}

                </tbody>

              </table>

              <div className="mt-6 flex gap-3">

                <button
                  className="bg-gray-600 text-white px-6 py-2 rounded"
                  onClick={goBack}
                >
                  Back
                </button>

              </div>

            </>
          )}

        </div>
      )}

    </div>
  );
}

